import {
  useMutation,
  useSuspenseQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { Suspense } from "react";
import { toast } from "sonner";

import { ConnectionForm } from "@/azure-devops/components/connection-form";
import { useTRPC } from "@/infrastructure/trpc/react";
import { AppLayout } from "@/shared/layout";
import { Skeleton } from "@/shared/ui/skeleton";

export const Route = createFileRoute(
  "/app/organization/$id/azdo/$connectionId/edit"
)({
  loader: async ({ context, params }) => {
    await context.queryClient
      .query(
        context.trpc.azureDevOps.getByIdWithCredentials.queryOptions(
          {
            organizationId: params.id,
            connectionId: params.connectionId,
          }
        )
      )
      .catch(() => undefined);
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { id: organizationId, connectionId } =
    Route.useParams();

  return (
    <AppLayout
      title="Edit Azure DevOps Connection"
      headerActions={null}
    >
      <div className="mx-auto max-w-2xl">
        <Suspense
          fallback={
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-64 w-full" />
            </div>
          }
        >
          <EditConnectionForm
            organizationId={organizationId}
            connectionId={connectionId}
          />
        </Suspense>
      </div>
    </AppLayout>
  );
}

function EditConnectionForm({
  organizationId,
  connectionId,
}: {
  organizationId: string;
  connectionId: string;
}) {
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data } = useSuspenseQuery(
    trpc.azureDevOps.getByIdWithCredentials.queryOptions({
      organizationId,
      connectionId,
    })
  );

  const updateConnection = useMutation(
    trpc.azureDevOps.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Azure DevOps connection updated");
        await queryClient.invalidateQueries(
          trpc.azureDevOps.list.queryOptions({
            organizationId,
          })
        );
        void navigate({
          to: "/app/organization/$id/settings/azure-devops",
          params: { id: organizationId },
        });
      },
      onError: (error) => {
        toast.error(
          error.message ?? "Failed to update connection"
        );
      },
    })
  );

  const connection = data?.connection;

  if (!connection) {
    return <div>Connection not found</div>;
  }

  return (
    <ConnectionForm
      organizationId={organizationId}
      mode="edit"
      initialValues={{
        name: connection.name,
        azureOrganization: connection.azureOrganization,
        personalAccessToken: connection.personalAccessToken,
      }}
      onSubmit={async (values) => {
        await updateConnection.mutateAsync({
          organizationId,
          connectionId,
          ...values,
        });
      }}
      isSubmitting={updateConnection.isPending}
      cardDescription="Update your Azure DevOps connection settings"
      submitButtonText="Save Changes"
      submittingButtonText="Saving..."
    />
  );
}
